import React from 'react';
import Papa from 'papaparse';
import { GroupedApplicationError } from '@tradingblock/types';
import { getLocalDateFromTimezone } from '@tradingblock/api';

interface ApplicationErrorsDownloadCSVProps {
  applicationErrors: GroupedApplicationError[];
}

export const ApplicationErrorsDownloadCSV: React.FC<ApplicationErrorsDownloadCSVProps> = ({ applicationErrors }) => {
  const prepareCSVData = (applicationErrors: GroupedApplicationError[]) => {
    const csvData: {
      message: string;
      count: number | string;
      mostRecent: string;
      ownerUsername: string;
      timeStamp: string;
      subRowOwnerUsername: string;
      subRowRepId: number | string;
    }[] = [];

    // We need to flatten the errorInfo array into separate rows underneath a parent row for each error message
    // the parent row will have the following columns: count, mostRecent, ownerUsername, message
    // the child rows will have the following columns: timeStamp, ownerUsername, repId
    applicationErrors.forEach(error => {
      const { count, mostRecent, ownerUsername, message, errorInfo } = error;

      // Add parent row
      csvData.push({
        message,
        count,
        mostRecent: getLocalDateFromTimezone(new Date(mostRecent)).format('MM/DD/YY h:mm:ss A'),
        ownerUsername,
        timeStamp: '',
        subRowOwnerUsername: '',
        subRowRepId: '',
      });

      // Add child rows
      errorInfo.forEach(subRow => {
        // format timeStamp in local time
        const timeStamp = getLocalDateFromTimezone(new Date(subRow.timeStamp)).format('MM/DD/YY h:mm:ss A');
        csvData.push({
          message: '',
          count: '',
          mostRecent: '',
          ownerUsername: '',
          timeStamp: timeStamp,
          subRowOwnerUsername: subRow.ownerUsername,
          subRowRepId: subRow.repId,
        });
      });

      // Add a visual separator (optional)
      csvData.push({
        message: '',
        count: '',
        mostRecent: '',
        ownerUsername: '',
        timeStamp: '',
        subRowOwnerUsername: '',
        subRowRepId: '',
      });
    });

    return csvData;
  };

  const downloadCSV = () => {
    const csvData = prepareCSVData(applicationErrors);

    // Define the columns for parent and child rows
    const fields = [
      'message',
      'count',
      'mostRecent',
      'ownerUsername',
      'timeStamp',
      'subRowOwnerUsername',
      'subRowRepId',
    ];

    // Convert the csvData to a CSV string using Papa.unparse with specified columns
    const csv = Papa.unparse(csvData, { columns: fields });

    // Create a new Blob object with the CSV string and specify the MIME type as CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary anchor element to facilitate the download
    const link = document.createElement('a');

    // Create a URL for the Blob object
    const url = URL.createObjectURL(blob);

    // Set the href attribute of the anchor element to the Blob URL
    link.setAttribute('href', url);

    // Set the download attribute of the anchor element to specify the file name
    link.setAttribute('download', 'application_errors.csv');

    // Append the anchor element to the document body
    document.body.appendChild(link);

    // Programmatically click the anchor element to trigger the download
    link.click();

    // Remove the anchor element from the document body
    document.body.removeChild(link);
  };

  prepareCSVData(applicationErrors);
  return (
    <button className="btn btn-outline-light" onClick={() => downloadCSV()}>
      <i className="fas fa-file-csv" /> Download CSV
    </button>
  );
};
